import L from 'leaflet';
import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import {
  Identifier, useTranslate, Datagrid, Confirm,
} from 'react-admin';
import { useEffect, useMemo, useState } from 'react';
import { useGetList, useRefresh, useNotify } from 'ra-core';
import { Box, TableHead } from '@mui/material';
import {
  hasItemPosition, itemToLatLng, securityItemsResource, appUsersResource,
} from '../util';
import { ItemCategorySettingsButton } from './ItemCategorySettingsButton';
import { BulkActionsSection } from './BulkActionsSection';
import { CreateItemSection } from './CreateItemSection';
import { FilterButton } from './FilterButton';
import { SearchInput } from './SearchInput';
import { SelectAllButton } from './SelectAllButton';
import { DataGridListItem } from './DataGridListItem';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { EditItemModal } from './EditItemModal';

export const InventoryItemsSidebar = (props: {
  itemsData: SecurityInventoryItem[];
  itemsLoading: boolean;
  categoriesData: SecurityInventoryCategory[];
  map: L.Map | null;
  focusedItemId: Identifier | null;
  setFocusedItemId: React.Dispatch<React.SetStateAction<Identifier | null>>;
  setFilteredItems: React.Dispatch<React.SetStateAction<SecurityInventoryItem[]>>;
}) => {

  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [mouseHoverId, setMouseHoverId] = useState<Identifier | null>(null);
  const [selectedIds, setSelectedIds] = useState<Identifier[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [editItemRecord, setEditItemRecord] = useState<SecurityInventoryItem | null>(null);

  const showBulkActions = selectedIds.length > 0;
  const setFilteredItems = props.setFilteredItems;

  // For all of the fetched resources, all of the data is fetched at once without pagination

  const { data: usersData } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  // Both search and filter are applied together
  // Note: the categoriesData is used to check if the selected user is in the category's peopleToNotify array
  const filteredItems = useMemo(() => {

    const list = props.itemsData ?? [];
    const q = searchTerm.trim().toLowerCase();

    // Map is used for faster lookup of peopleToNotify by categoryId
    const peopleToNotifyByCategoryId = new Map<string, Set<string>>();
    for (const category of props.categoriesData ?? []) {

      peopleToNotifyByCategoryId.set(
        category._id,
        new Set(
          (category.peopleToNotify ?? []).map((entry) => (typeof entry === 'string' ? entry : entry._id)),
        ),
      );

    }
    return list.filter((item) => {

      const categoryId = item.category._id;

      if (selectedCategory && categoryId !== selectedCategory) return false;
      if (selectedUser) {

        const peopleToNotifySet = peopleToNotifyByCategoryId.get(categoryId);
        if (!peopleToNotifySet?.has(selectedUser)) return false;

      }

      if (q && !(item.name ?? '').toLowerCase().includes(q)) return false;
      return true;

    });

  }, [props.itemsData, selectedCategory, selectedUser, searchTerm, props.categoriesData]);

  const focusItemFromList = (itemId: Identifier, item: SecurityInventoryItem) => {

    props.setFocusedItemId(itemId);
    if (hasItemPosition(item) && props.map) {

      const latLng = L.latLng(itemToLatLng(item));
      if (!props.map.getBounds().contains(latLng)) {

        props.map.flyTo(latLng);

      }

    }

  };

  const handleDeleteItems = async () => {

    if (selectedIds.length === 0) return;
    try {

      await xgacDataProvider.deleteMany(securityItemsResource, { ids: selectedIds });
      notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: selectedIds.length } });
      refresh();
      if (props.focusedItemId && selectedIds.includes(props.focusedItemId)) {

        props.setFocusedItemId(null);

      }
      setSelectedIds([]);
      setDeleteDialogOpen(false);

    } catch {

      notify('ra.notification.http_error', { type: 'warning' });
      setDeleteDialogOpen(false);

    }

  };

  useEffect(() => {

    setFilteredItems(filteredItems);

  }, [filteredItems, setFilteredItems]);

  useEffect(() => {

    if (!props.focusedItemId) return;
    requestAnimationFrame(() => {

      document.getElementById(`inventory-item-${props.focusedItemId}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });

    });

  }, [props.focusedItemId]);

  return (
    <>
      <div className="button-sidebar-div">
        <div className="button-sidebar-add">
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <ItemCategorySettingsButton />
          </Box>
          <h4>
            <span>{showBulkActions ? translate('resources.buttons.text.actions_with_selected') : translate('menu.items.security_inventory')}</span>
          </h4>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showBulkActions && <BulkActionsSection onDelete={() => setDeleteDialogOpen(true)}/>}
            {!showBulkActions && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CreateItemSection
                categories={props.categoriesData || []}
                map={props.map}
              />
              <FilterButton
                categories={props.categoriesData || []}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                users={usersData || []}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}>
                <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              </FilterButton>
            </Box>}
            {showBulkActions && <SelectAllButton selectedIds={selectedIds} setSelectedIds={setSelectedIds} items={props.itemsData} />}
          </Box>
        </div>
        <div className="button-sidebar-grid">
          <Datagrid
            resource={securityItemsResource}
            data={filteredItems}
            isLoading={props.itemsLoading}
            total={filteredItems.length}
            empty={
              <Box sx={{
                px: 2, py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14,
              }}>
                {translate('resources.security-inventory-items.text.no_results')}
              </Box>
            }
            bulkActionButtons={false}
            rowClick={false}
            header={<TableHead />}
            rowStyle={(record: SecurityInventoryItem, index: number) => {

              if (selectedIds.includes(record._id)) {

                return {
                  backgroundColor: '#9FD2B6',
                };

              }
              if (props.focusedItemId === record._id) {

                return {
                  backgroundColor: index % 2 === 0 ? '#E8F4EC' : '#D8EDE4',
                  outline: '2px solid #2E7D57',
                  outlineOffset: -2,
                };

              }
              return {
                backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
              };

            }}
            sx={{
              '& .RaDatagrid-expandIconCell': {
                display: 'none',
              },
            }}
          >
            <DataGridListItem
              categories={props.categoriesData || []}
              mouseHoverId={mouseHoverId}
              setMouseHoverId={setMouseHoverId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onFocus={focusItemFromList}
              setEditItemRecord={setEditItemRecord}
              setEditItemModalOpen={setEditItemModalOpen}
            />
          </Datagrid>
        </div>
      </div>
      <Confirm
        isOpen={deleteDialogOpen}
        onConfirm={handleDeleteItems}
        onClose={() => setDeleteDialogOpen(false)}
        title={translate('resources.security-inventory-items.text.delete_dialog_title')}
        content={translate('resources.security-inventory-items.text.delete_content')}
      />
      <EditItemModal
        record={editItemRecord}
        categories={props.categoriesData || []}
        open={editItemModalOpen}
        onClose={() => {

          setEditItemModalOpen(false);
          setEditItemRecord(null);

        }}
      />
    </>
  );

};
